import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { Video, Search } from 'lucide-react';
import { useSelectedMetrics, isMetricSelected, getCosClassName, getCpaClassName } from '../utils/metricsUtils';

/**
 * Composant table modulaire des vidéos YouTube Ads
 * Optimisé avec React.memo et useMemo pour les performances
 */
const YoutubeVideosTable = React.memo(({ 
  rawData,
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedVideoFilter,
  setSelectedVideoFilter,
  selectedCampaignTypeFilter,
  selectedAccount = null,
  // Nouveaux props pour récupérer les données Performance Max
  selectedPeriod,
  customStartDate,
  customEndDate,
  token,
  setError,
  dashboardContext = 'youtube', // 'youtube' | 'performance-max'
  // Nouveau prop pour exposer les données traitées
  onDataProcessed
}) => {
  // États locaux pour cette table
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [metricsMode, setMetricsMode] = useState('performance'); // 'performance' ou 'engagement'
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'manual', 'auto'
  const [campaignFilter, setCampaignFilter] = useState('all'); // 'all' ou campaignId spécifique
  const [searchFilter, setSearchFilter] = useState(''); // Filtre de recherche par titre
  
  // États pour les données Performance Max
  const [performanceMaxVideos, setPerformanceMaxVideos] = useState([]);
  const [performanceMaxDailyData, setPerformanceMaxDailyData] = useState([]); // NOUVEAU: données journalières Performance Max
  const [loadingPmax, setLoadingPmax] = useState(false);
  const [pmaxLoadAttempted, setPmaxLoadAttempted] = useState(false); // Flag pour savoir si on a tenté de charger Performance Max
  
  // Récupérer les métriques sélectionnées avec mise à jour automatique
  const selectedMetrics = useSelectedMetrics();
  
  // Récupérer les données Performance Max
  useEffect(() => {
    if (!selectedAccount || !selectedPeriod || !token) {
      setPmaxLoadAttempted(false); // Réinitialiser si pas de paramètres
      return;
    }
    
    const loadPerformanceMaxVideos = async () => {
      try {
        setLoadingPmax(true);
        setPmaxLoadAttempted(true); // Marquer qu'on a commencé le chargement Performance Max
        const requestBody = {
          sources: [selectedAccount.id],
          period: selectedPeriod,
          campaignFilter: null,
          campaignTypeFilter: null
        };

        if (selectedPeriod === 'CUSTOM') {
          if (!customStartDate || !customEndDate) {
            return;
          }
          requestBody.customStartDate = customStartDate;
          requestBody.customEndDate = customEndDate;
        }
        
        const response = await axios.post(
          `${config.API_URL}/api/dashboard/components/performance-max/videos`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.success && response.data.data && response.data.data.videos) {
          const pmaxVideos = response.data.data.videos.map(video => ({
            ...video,
            id: video.youtubeVideoId || video.id,
            campaignType: 'PERFORMANCE_MAX',
            isPmaxVideo: true,
            ctr: video.impressions > 0 ? (video.clicks / video.impressions * 100) : 0,
            cpc: video.clicks > 0 ? (video.cost / video.clicks) : 0,
            interactions: video.engagements || 0,
            taux_engagement: video.impressions > 0 ? (video.engagements / video.impressions * 100) : 0
          }));
          
          setPerformanceMaxVideos(pmaxVideos);
          
          // NOUVEAU: Capturer les données journalières Performance Max
          const pmaxDailyData = response.data.data?.dailyData || [];
          setPerformanceMaxDailyData(pmaxDailyData);
          console.log(`📅 [YouTubeVideosTable] Performance Max daily data reçues: ${pmaxDailyData.length} jours`);
        } else {
          setPerformanceMaxVideos([]);
          setPerformanceMaxDailyData([]);
        }
      } catch (error) {
        if (setError) setError('Erreur lors du chargement des vidéos Performance Max');
        setPerformanceMaxVideos([]);
        setPerformanceMaxDailyData([]);
      } finally {
        setLoadingPmax(false);
      }
    };

    loadPerformanceMaxVideos();
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, token]);
  
  // Définition des métriques par mode
  const engagementMetrics = ['impressions', 'vtr', 'views', 'cpv', 'cpm', 'engagements', 'taux_engagement'];
  const performanceMetrics = ['impressions', 'clics', 'cout', 'ctr', 'cpc', 'conv', 'taux_conv', 'cpa', 'valeur_conv', 'cos'];
  
  // Fonction pour vérifier si une métrique doit être affichée selon le mode
  const isMetricVisibleInMode = (metricKey) => {
    if (metricsMode === 'engagement') {
      // En mode engagement, forcer l'affichage des métriques d'engagement même si pas sélectionnées
      return engagementMetrics.includes(metricKey);
    }
    return performanceMetrics.includes(metricKey) && isMetricSelected(metricKey);
  };

  // Extraction des données de vidéos (memoized) - combine YouTube classiques et Performance Max
  const videosData = useMemo(() => {
    if (!rawData) return [];
    const accountData = Object.values(rawData).find(data => data.videos);
    let videos = accountData?.videos || [];
    
    // Récupérer aussi les données temporelles
    const dailyData = accountData?.dailyData || [];
    console.log(`📅 [YouTubeVideosTable] Données temporelles reçues: ${dailyData.length} jours`);
    
    // Ajouter la détection du type de source pour les vidéos YouTube classiques  
    videos = videos.map(video => {
      // Si c'est une vidéo Performance Max, garder son sourceType
      if (video.sourceType) {
        return video;
      }
      
      // Pour les vidéos YouTube classiques, déterminer le type basé sur le titre
      const title = video.title || video.name || '';
      const titleLower = title.toLowerCase();
      
      // Logique similaire à Performance Max pour détecter les vidéos automatiques
      let isAutoGenerated = false;
      
      // Vérifier si le titre commence par des patterns automatiques courants
      if (titleLower.startsWith('adv') || 
          titleLower.startsWith('ad_') ||
          titleLower.includes('auto-generated') ||
          titleLower.includes('autogenerated') ||
          title.length < 10) { // Titres très courts souvent automatiques
        isAutoGenerated = true;
      }
      
      return {
        ...video,
        sourceType: isAutoGenerated ? 'AUTO_GENERATED' : 'MANUAL'
      };
    });

    // Fusionner avec les vidéos Performance Max
    if (performanceMaxVideos.length > 0) {
      // Éviter les doublons basés sur l'ID YouTube
      const existingVideoIds = new Set(videos.map(v => v.id));
      const newPmaxVideos = performanceMaxVideos.filter(v => v.id && !existingVideoIds.has(v.id));
      videos = [...videos, ...newPmaxVideos];
    }

    // Debug: afficher tous les campaignType disponibles
    const campaignTypes = [...new Set(videos.map(v => v.campaignType))];
    console.log('🎬 [DEBUG] CampaignTypes disponibles:', campaignTypes);
    console.log('🎬 [DEBUG] Vidéos DEMAND_GEN:', videos.filter(v => v.campaignType === 'DEMAND_GEN').length);
    
    // Filtrer pour n'afficher que la vidéo sélectionnée si une vidéo est filtrée
    if (selectedVideoFilter) {
      videos = videos.filter(video => 
        video.id === selectedVideoFilter.id || 
        video.videoId === selectedVideoFilter.id ||
        String(video.id) === String(selectedVideoFilter.id) ||
        video.title === selectedVideoFilter.title
      );
    }
    
    // Filtrer par type de campagne si un type est sélectionné
    if (selectedCampaignTypeFilter) {
      videos = videos.filter(video => 
        video.advertisingChannelType === selectedCampaignTypeFilter.type
      );
    }
    
    // Filtrer par campagne spécifique (conversion en string pour éviter les problèmes de comparaison number/string)
    if (campaignFilter !== 'all') {
      videos = videos.filter(video =>
        String(video.campaignId) === String(campaignFilter) ||
        video.campaignName === campaignFilter
      );
      // Debug: afficher les campaignType des vidéos filtrées
      console.log('🔍 [DEBUG] Vidéos après filtre campagne:', videos.map(v => ({ title: v.videoTitle, campaignType: v.campaignType, campaignName: v.campaignName })));
    }
    
    // Filtrer par type de source (manuel/automatique)
    if (sourceFilter !== 'all') {
      videos = videos.filter(video => {
        if (sourceFilter === 'manual') {
          return video.sourceType === 'MANUAL';
        } else if (sourceFilter === 'auto') {
          return video.sourceType === 'AUTO_GENERATED';
        }
        return true;
      });
    }
    
    // Filtrer par titre de vidéo (recherche)
    if (searchFilter.trim()) {
      const searchTerm = searchFilter.toLowerCase().trim();
      videos = videos.filter(video => 
        (video.title || video.name || '').toLowerCase().includes(searchTerm)
      );
    }
    
    return videos;
  }, [rawData, selectedVideoFilter, selectedCampaignTypeFilter, performanceMaxVideos, sourceFilter, campaignFilter, searchFilter]);

  // Extraction et fusion des données temporelles (memoized)
  const dailyData = useMemo(() => {
    if (!rawData) return [];
    
    // Récupérer les données temporelles du YouTube classique
    const accountData = Object.values(rawData).find(data => data.videos);
    const youtubeDailyData = accountData?.dailyData || [];
    
    // Récupérer les données temporelles de Performance Max
    const pmaxDailyData = performanceMaxDailyData;
    
    // Si pas de données YouTube classiques et pas de Performance Max, retourner vide
    if (youtubeDailyData.length === 0 && pmaxDailyData.length === 0) {
      return [];
    }
    
    // Fusionner les données journalières des deux sources
    const combinedDailyMap = new Map();
    
    // Ajouter les données YouTube classiques
    youtubeDailyData.forEach(dayData => {
      combinedDailyMap.set(dayData.date, {
        date: dayData.date,
        impressions: dayData.impressions || 0,
        clicks: dayData.clicks || 0,
        views: dayData.views || 0,
        cost: dayData.cost || 0,
        conversions: dayData.conversions || 0,
        conversionsValue: dayData.conversionsValue || 0,
        interactions: dayData.interactions || 0
      });
    });
    
    // Ajouter les données Performance Max (fusionner par date)
    pmaxDailyData.forEach(dayData => {
      if (!combinedDailyMap.has(dayData.date)) {
        combinedDailyMap.set(dayData.date, {
          date: dayData.date,
          impressions: 0,
          clicks: 0,
          views: 0,
          cost: 0,
          conversions: 0,
          conversionsValue: 0,
          interactions: 0
        });
      }
      
      const combinedDayData = combinedDailyMap.get(dayData.date);
      combinedDayData.impressions += dayData.impressions || 0;
      combinedDayData.clicks += dayData.clicks || 0;
      combinedDayData.views += dayData.views || 0;
      combinedDayData.cost += dayData.cost || 0;
      combinedDayData.conversions += dayData.conversions || 0;
      combinedDayData.conversionsValue += dayData.conversionsValue || 0;
      combinedDayData.interactions += dayData.interactions || 0;
    });
    
    // Convertir en tableau trié par date
    const finalDailyData = Array.from(combinedDailyMap.values()).sort((a, b) => a.date.localeCompare(b.date));
    
    console.log(`📅 [YouTubeVideosTable] Données temporelles fusionnées: ${youtubeDailyData.length} YouTube + ${pmaxDailyData.length} PMAX = ${finalDailyData.length} jours`);
    
    return finalDailyData;
  }, [rawData, performanceMaxDailyData]);

  // Liste unique des campagnes disponibles (memoized)
  const availableCampaigns = useMemo(() => {
    if (!rawData) return [];
    const accountData = Object.values(rawData).find(data => data.videos);
    let allVideos = accountData?.videos || [];
    
    // Ajouter les vidéos Performance Max fusionnées
    if (performanceMaxVideos.length > 0) {
      const existingVideoIds = new Set(allVideos.map(v => v.id));
      const newPmaxVideos = performanceMaxVideos.filter(v => v.id && !existingVideoIds.has(v.id));
      allVideos = [...allVideos, ...newPmaxVideos];
    }
    
    // Extraire les campagnes uniques - essayer différents champs
    const campaignsSet = new Set();
    allVideos.forEach((video, index) => {
      // Essayer différentes propriétés pour le nom de campagne
      const possibleNames = [
        video.campaignName,
        video.campaign_name,
        video.campaign?.name,
        ...(video.campaignNames || []) // Pour les vidéos Performance Max
      ].filter(Boolean);
      
      // Pour les campagnes, utiliser le nom comme ID si pas d'ID disponible
      const possibleIds = [
        video.campaignId,
        video.campaign_id,
        video.campaign?.id
      ].filter(Boolean);
      
      if (possibleNames.length > 0) {
        // Filtrer seulement les noms vraiment génériques, mais garder les campagnes VIDEO spécifiques
        const realCampaignName = possibleNames[0];
        const isGeneric = realCampaignName === 'Toutes campagnes' || 
                         realCampaignName === 'Toutes campagnes VIDEO' ||
                         !realCampaignName ||
                         realCampaignName.trim() === '';
        
        if (realCampaignName && !isGeneric) {
          campaignsSet.add(JSON.stringify({
            id: possibleIds.length > 0 ? possibleIds[0] : realCampaignName, // Utiliser le nom comme ID si pas d'ID
            name: realCampaignName
          }));
        }
      }
    });
    
    const campaigns = Array.from(campaignsSet)
      .map(str => JSON.parse(str))
      .sort((a, b) => a.name.localeCompare(b.name));
    
    return campaigns;
  }, [rawData, performanceMaxVideos]);

  // Fonction de tri memoized
  const sortedVideos = useMemo(() => {
    if (!videosData.length) return [];

    const sorted = [...videosData];
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      // Calcul spécial pour CPA
      if (sortConfig.key === 'cpa') {
        aValue = a.conversions > 0 ? (a.cost / a.conversions) : Number.MAX_VALUE;
        bValue = b.conversions > 0 ? (b.cost / b.conversions) : Number.MAX_VALUE;
      } else {
        aValue = a[sortConfig.key] || 0;
        bValue = b[sortConfig.key] || 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [videosData, sortConfig]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!videosData.length) return {};

    const baseTotals = videosData.reduce((acc, video) => ({
      impressions: acc.impressions + (video.impressions || 0),
      views: acc.views + (video.views || 0),
      clicks: acc.clicks + (video.clicks || 0),
      cost: acc.cost + (video.cost || 0),
      conversions: acc.conversions + (video.conversions || 0),
      conversionsValue: acc.conversionsValue + (video.conversionsValue || 0),
      interactions: acc.interactions + (video.interactions || 0),
      tauxEngagementTotal: acc.tauxEngagementTotal + (video.taux_engagement || 0),
      countVideos: acc.countVideos + 1 // Pour calculer la moyenne du taux d'engagement
    }), { impressions: 0, views: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, interactions: 0, tauxEngagementTotal: 0, countVideos: 0 });
    
    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      vtr: baseTotals.impressions > 0 ? (baseTotals.views / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      cpv: baseTotals.views > 0 ? (baseTotals.cost / baseTotals.views) : 0,
      cpm: baseTotals.impressions > 0 ? (baseTotals.cost / baseTotals.impressions * 1000) : 0,
      tauxEngagement: baseTotals.countVideos > 0 ? (baseTotals.tauxEngagementTotal / baseTotals.countVideos) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [videosData]);

  // Exposer les données traitées via callback
  useEffect(() => {
    // Ne pas envoyer les données si :
    // - Les totaux sont vides ou pas calculés
    // - Les vidéos ne sont pas chargées
    // - Performance Max n'a pas encore été tenté OU est encore en cours de chargement
    if (onDataProcessed && 
        videosData && 
        totals && 
        Object.keys(totals).length > 0 && 
        videosData.length > 0 &&
        pmaxLoadAttempted && // NOUVEAU: s'assurer que Performance Max a été tenté
        !loadingPmax) { // IMPORTANT: attendre que Performance Max soit fini
      console.log('📊 [YouTubeVideosTable] Envoi des totaux FINAUX (Performance Max inclus):', totals);
      console.log(`📅 [YouTubeVideosTable] Envoi des données temporelles: ${dailyData.length} jours`);
      onDataProcessed({ videosData, totals, dailyData }); // Inclure dailyData
    }
  }, [videosData, totals, dailyData, loadingPmax, pmaxLoadAttempted]);

  // Configuration de pagination
  const itemsPerPage = 20;
  const totalVideos = sortedVideos.length;
  const totalPages = Math.ceil(totalVideos / itemsPerPage);

  // Réinitialiser la page quand les données changent
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Données paginées memoized
  const paginatedVideos = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalVideos);
    return sortedVideos.slice(startIndex, endIndex);
  }, [sortedVideos, currentPage, itemsPerPage, totalVideos]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Réinitialiser à la première page
  }, [sortConfig]);

  // Handler de pagination memoized
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, newPage)));
  }, [totalPages]);

  // Handlers pour le tooltip
  const handleTooltipShow = useCallback((e, tooltipType) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 10
    });
    setActiveTooltip(tooltipType);
  }, []);

  const handleTooltipHide = useCallback(() => {
    setActiveTooltip(null);
  }, []);

  // Contenu des tooltips
  const getTooltipContent = (tooltipType) => {
    switch (tooltipType) {
      case 'views':
        return `Une vue TrueView (anciennement appelée "vue") est comptabilisée lorsqu'une personne choisit de visionner votre annonce vidéo ou d'interagir avec elle au lieu de l'ignorer ou de continuer à faire défiler la page.

La durée de visionnage varie selon le format d'annonce :
• Annonces InStream désactivables : 30 secondes
• Annonces vidéo In-Feed : 10 secondes
• Annonces Shorts : 10 secondes

Une vue TrueView est aussi comptabilisée lorsqu'une personne regarde l'intégralité de l'annonce vidéo, quelle que soit sa durée, ou lorsqu'elle clique ou appuie dessus.

Les vues TrueView indiquent un fort intérêt pour votre produit ou votre marque. Les personnes qui regardent vos annonces vidéo ou interagissent avec elles sont plus susceptibles de rechercher votre marque et de l'envisager activement lorsqu'elles sont sur le point de passer à l'action.`;
      case 'taux_engagement':
        return `Le taux d'engagement sert à mesurer la fréquence à laquelle les utilisateurs interagissent avec votre annonce lorsqu'ils la visualisent. Il vous permet ainsi de mieux évaluer son efficacité. Il est calculé en divisant le nombre d'engagements par le nombre total d'impressions.`;
      case 'conv':
        return `"Conversions" affiche le nombre de conversions enregistrées après des interactions avec vos annonces (comme un clic sur une annonce textuelle ou le visionnage d'une annonce vidéo) pour toutes les actions que vous avez incluses dans cette colonne.

Le paramètre "Inclure dans 'Conversions'" détermine pour chaque action si les conversions associées sont incluses dans la colonne.

Les stratégies d'enchères intelligentes optimisent les conversions dans cette colonne.`;
      case 'cpm':
        return `Coût moyen pour mille impressions (CPM)

Il s'agit du montant moyen qui vous a été facturé pour 1 000 impressions.`;
      case 'cpv':
        return `Le coût par vue moyen TrueView (anciennement appelé "coût par vue moyen") correspond au montant moyen qui vous a été facturé pour les vues TrueView de cette campagne. Il est calculé en divisant le coût total par le nombre total de vues TrueView.`;
      default:
        return '';
    }
  };

  // Vérifier s'il y a des données brutes disponibles
  const hasRawData = rawData && Object.values(rawData).some(data => data.videos && data.videos.length > 0);
  const hasPerformanceMaxData = performanceMaxVideos.length > 0;
  
  // Ne pas rendre si aucune donnée disponible (même brute)
  if (!hasRawData && !hasPerformanceMaxData) return null;

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalVideos);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Video size={16} />&nbsp;{dashboardContext === 'performance-max' ? 'Vidéos Performance Max' : 'Vidéos YouTube'}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Champ de recherche par titre */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}><Search size={14} /> Rechercher :</span>
            <input
              type="text"
              placeholder="Titre de vidéo..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              style={{
                padding: '4px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.75rem',
                backgroundColor: 'white',
                minWidth: '180px'
              }}
            />
          </div>
          {/* Filtre par campagne */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Campagne :</span>
            <select
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
              style={{
                padding: '4px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.75rem',
                backgroundColor: 'white',
                minWidth: '150px'
              }}
            >
              <option value="all">Toutes les campagnes</option>
              {availableCampaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Filtre par type de source */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Vidéos :</span>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              style={{
                padding: '4px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.75rem',
                backgroundColor: 'white'
              }}
            >
              <option value="all">Toutes</option>
              <option value="manual">👤 Manuelles</option>
              <option value="auto">🤖 Automatiques</option>
            </select>
          </div>
          
          <select 
            value={metricsMode} 
            onChange={(e) => setMetricsMode(e.target.value)}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '0.9em',
              cursor: 'pointer',
              backgroundColor: 'white'
            }}
          >
            <option value="performance">📊 Performance</option>
            <option value="engagement">💡 Engagement</option>
          </select>
          {selectedVideoFilter && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px', 
              padding: '4px 8px', 
              background: '#e3f2fd', 
              borderRadius: '4px',
              fontSize: '0.9em'
            }}>
              <span>Filtre: {selectedVideoFilter.title || selectedVideoFilter.name}</span>
              <button 
                onClick={() => setSelectedVideoFilter(null)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer',
                  padding: '2px',
                  color: '#666'
                }}
                title="Supprimer le filtre"
              >
                ✕
              </button>
            </div>
          )}
          <button 
            onClick={onToggleVisibility}
            className="section-toggle-btn"
            title={isVisible ? "Masquer la section" : "Afficher la section"}
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>
      </div>

      {isVisible && (
        <>
          {videosData.length === 0 ? (
            <div style={{
              padding: '48px',
              textAlign: 'center',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              margin: '16px 0'
            }}>
              <div style={{ fontSize: '2em', marginBottom: '16px', opacity: 0.5 }}>🔍</div>
              <h3 style={{ 
                margin: '0 0 8px 0', 
                color: '#6c757d',
                fontSize: '1.1em',
                fontWeight: '500'
              }}>
                Aucun résultat trouvé
              </h3>
              <p style={{ 
                margin: '0', 
                color: '#6c757d',
                fontSize: '0.9em'
              }}>
                Aucune vidéo ne correspond aux filtres sélectionnés.
              </p>
              {(sourceFilter !== 'all' || campaignFilter !== 'all' || searchFilter.trim()) && (
                <button 
                  onClick={() => {
                    setSourceFilter('all');
                    setCampaignFilter('all');
                    setSearchFilter('');
                  }}
                  style={{
                    marginTop: '16px',
                    padding: '8px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9em'
                  }}
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="campaigns-table-wrapper">
            <table 
              key={selectedMetrics.filter(m => m.selected).map(m => m.key).join(',')} 
              className="campaigns-table" 
              style={{ 
                width: '100%', 
                tableLayout: 'auto', 
                borderCollapse: 'collapse'
              }}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('title')} className="sortable">
                    Vidéo {sortConfig.key === 'title' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {isMetricVisibleInMode('impressions') && (
                    <th onClick={() => handleSort('impressions')} className="sortable numeric">
                      Impr. {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('ctr') && (
                    <th onClick={() => handleSort('ctr')} className="sortable numeric">
                      CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('vtr') && (
                    <th onClick={() => handleSort('vtr')} className="sortable numeric">
                      Taux de vue {sortConfig.key === 'vtr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('views') && (
                    <th 
                      onClick={() => handleSort('views')} 
                      className="sortable numeric"
                      onMouseEnter={(e) => handleTooltipShow(e, 'views')}
                      onMouseLeave={handleTooltipHide}
                      style={{ position: 'relative' }}
                    >
                      Vues {sortConfig.key === 'views' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('cpv') && (
                    <th 
                      onClick={() => handleSort('cpv')} 
                      className="sortable numeric"
                      onMouseEnter={(e) => handleTooltipShow(e, 'cpv')}
                      onMouseLeave={handleTooltipHide}
                      style={{ position: 'relative' }}
                    >
                      CPV {sortConfig.key === 'cpv' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('cpm') && (
                    <th 
                      onClick={() => handleSort('cpm')} 
                      className="sortable numeric"
                      onMouseEnter={(e) => handleTooltipShow(e, 'cpm')}
                      onMouseLeave={handleTooltipHide}
                      style={{ position: 'relative' }}
                    >
                      CPM {sortConfig.key === 'cpm' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('engagements') && (
                    <th onClick={() => handleSort('engagements')} className="sortable numeric">
                      Engagements {sortConfig.key === 'engagements' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('taux_engagement') && (
                    <th 
                      onClick={() => handleSort('taux_engagement')} 
                      className="sortable numeric"
                      onMouseEnter={(e) => handleTooltipShow(e, 'taux_engagement')}
                      onMouseLeave={handleTooltipHide}
                      style={{ position: 'relative' }}
                    >
                      % d'engagement {sortConfig.key === 'taux_engagement' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('clics') && (
                    <th onClick={() => handleSort('clicks')} className="sortable numeric">
                      Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('cout') && (
                    <th onClick={() => handleSort('cost')} className="sortable numeric">
                      Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('cpc') && (
                    <th onClick={() => handleSort('cpc')} className="sortable numeric">
                      CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('conv') && (
                    <th 
                      onClick={() => handleSort('conversions')} 
                      className="sortable numeric"
                      onMouseEnter={(e) => handleTooltipShow(e, 'conv')}
                      onMouseLeave={handleTooltipHide}
                      style={{ position: 'relative' }}
                    >
                      Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('taux_conv') && (
                    <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                      Taux Conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('cpa') && (
                    <th onClick={() => handleSort('cpa')} className="sortable numeric">
                      CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('valeur_conv') && (
                    <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                      Valeur Conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isMetricVisibleInMode('cos') && (
                    <th onClick={() => handleSort('cos')} className="sortable numeric">
                      COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {paginatedVideos.map((video, index) => {
                  const cos = (video.conversionsValue || 0) > 0 ? (video.cost / (video.conversionsValue || 1) * 100) : 0;
                  const conversionRate = video.clicks > 0 ? (video.conversions / video.clicks * 100) : 0;
                  const cpa = video.conversions > 0 ? (video.cost / video.conversions) : 0;
                  const vtr = video.impressions > 0 ? (video.views / video.impressions * 100) : 0;
                  const cpv = video.views > 0 ? (video.cost / video.views) : 0;
                  const cpm = video.impressions > 0 ? (video.cost / video.impressions * 1000) : 0;
                  const tauxEngagement = video.taux_engagement || 0; // Taux d'engagement basé sur les quartiles YouTube
                  
                  return (
                    <tr 
                      key={`${video.id || video.adId || index}-${index}`}
                      onClick={() => setSelectedVideoFilter(video)} 
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: selectedVideoFilter?.id === video.id ? '#e3f2fd' : 'inherit'
                      }}
                      title="Cliquer pour filtrer tous les composants par cette vidéo"
                    >
                      <td className="campaign-name" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <a 
                          href={`https://youtube.com/watch?v=${video.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ flexShrink: 0, display: 'block' }}
                          title="Voir la vidéo sur YouTube"
                        >
                          <img 
                            src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                            alt="Miniature vidéo"
                            style={{ 
                              width: '80px', 
                              height: '45px', 
                              borderRadius: '4px',
                              objectFit: 'cover',
                              cursor: 'pointer',
                              border: '1px solid #ddd'
                            }}
                            onError={(e) => {
                              // Fallback vers une image par défaut si la miniature n'existe pas
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iNDUiIHZpZXdCb3g9IjAgMCA4MCA0NSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjQ1IiBmaWxsPSIjRjBGMEYwIi8+CjxwYXRoIGQ9Ik0zMiAyMUwzOCAyNUwzMiAyOVYyMVoiIGZpbGw9IiM5OTk5OTkiLz4KPC9zdmc+';
                            }}
                          />
                        </a>
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: '500' }}>
                              {video.title || video.name}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <a 
                              href={`https://youtube.com/watch?v=${video.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                fontSize: '0.8em', 
                                color: '#1976d2', 
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title="Voir sur YouTube"
                            >
                              🔗 Voir sur YouTube
                            </a>
                            {/* Badge Performance Max */}
                            {(video.isPmaxVideo || 
                              video.campaignType === 'PERFORMANCE_MAX' || 
                              video.campaignName?.includes('[PERFMAX]') || 
                              video.campaignNames?.some(name => name.includes('[PERFMAX]'))) && (
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '1px 4px',
                                  margin: '0 2px 0 0',
                                  borderRadius: '8px',
                                  fontSize: '0.65em',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  backgroundColor: '#e3f2fd',
                                  color: '#1565c0'
                                }}
                                title="Vidéo Performance Max"
                              >
                                PMAX
                              </span>
                            )}
                            {/* Badge Demand Gen */}
                            {video.campaignType === 'DEMAND_GEN' && (
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '1px 4px',
                                  margin: '0 2px 0 0',
                                  borderRadius: '8px',
                                  fontSize: '0.65em',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  backgroundColor: '#fff3e0',
                                  color: '#e65100'
                                }}
                                title="Vidéo Demand Gen"
                              >
                                DEMAND_GEN
                              </span>
                            )}
                            {/* Badge YouTube (campagnes vidéo classiques) */}
                            {video.campaignType === 'VIDEO' && (
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '1px 4px',
                                  margin: '0 2px 0 0',
                                  borderRadius: '8px',
                                  fontSize: '0.65em',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  backgroundColor: '#ffebee',
                                  color: '#c62828'
                                }}
                                title="Campagne YouTube classique"
                              >
                                VIDEO
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      {isMetricVisibleInMode('impressions') && (
                        <td className="numeric">{formatNumber(video.impressions || 0)}</td>
                      )}
                      {isMetricVisibleInMode('ctr') && (
                        <td className="numeric">{formatNumber(video.ctr || 0, 2)}%</td>
                      )}
                      {isMetricVisibleInMode('vtr') && (
                        <td className="numeric">{formatNumber(vtr, 2)}%</td>
                      )}
                      {isMetricVisibleInMode('views') && (
                        <td className="numeric">{formatNumber(video.views || 0)}</td>
                      )}
                      {isMetricVisibleInMode('cpv') && (
                        <td className="numeric">€{formatNumber(cpv, 3)}</td>
                      )}
                      {isMetricVisibleInMode('cpm') && (
                        <td className="numeric">€{formatNumber(cpm, 2)}</td>
                      )}
                      {isMetricVisibleInMode('engagements') && (
                        <td className="numeric">{formatNumber(video.interactions || 0)}</td>
                      )}
                      {isMetricVisibleInMode('taux_engagement') && (
                        <td className="numeric">{formatNumber(tauxEngagement, 2)}%</td>
                      )}
                      {isMetricVisibleInMode('clics') && (
                        <td className="numeric">{formatNumber(video.clicks || 0)}</td>
                      )}
                      {isMetricVisibleInMode('cout') && (
                        <td className="numeric">€{formatNumber(video.cost || 0, 2)}</td>
                      )}
                      {isMetricVisibleInMode('cpc') && (
                        <td className="numeric">€{formatNumber(video.cpc || 0, 2)}</td>
                      )}
                      {isMetricVisibleInMode('conv') && (
                        <td className="numeric">{formatNumber(video.conversions || 0, 1)}</td>
                      )}
                      {isMetricVisibleInMode('taux_conv') && (
                        <td className="numeric">{formatNumber(conversionRate, 2)}%</td>
                      )}
                      {isMetricVisibleInMode('cpa') && (
                        <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', 'youtube_videos')}`}>{cpa >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}</td>
                      )}
                      {isMetricVisibleInMode('valeur_conv') && (
                        <td className="numeric">€{formatNumber(video.conversionsValue || 0, 2)}</td>
                      )}
                      {isMetricVisibleInMode('cos') && (
                        <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', 'youtube_videos')}`}>
                          {formatNumber(cos, 1)}%
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr className="totals-row">
                  <td className="campaign-name"><strong>TOTAL</strong></td>
                  {isMetricVisibleInMode('impressions') && (
                    <td className="numeric"><strong>{formatNumber(totals.impressions)}</strong></td>
                  )}
                  {isMetricVisibleInMode('ctr') && (
                    <td className="numeric"><strong>{formatNumber(totals.ctr, 2)}%</strong></td>
                  )}
                  {isMetricVisibleInMode('vtr') && (
                    <td className="numeric"><strong>{formatNumber(totals.vtr, 2)}%</strong></td>
                  )}
                  {isMetricVisibleInMode('views') && (
                    <td className="numeric"><strong>{formatNumber(totals.views)}</strong></td>
                  )}
                  {isMetricVisibleInMode('cpv') && (
                    <td className="numeric"><strong>€{formatNumber(totals.cpv, 3)}</strong></td>
                  )}
                  {isMetricVisibleInMode('cpm') && (
                    <td className="numeric"><strong>€{formatNumber(totals.cpm, 2)}</strong></td>
                  )}
                  {isMetricVisibleInMode('engagements') && (
                    <td className="numeric"><strong>{formatNumber(totals.interactions)}</strong></td>
                  )}
                  {isMetricVisibleInMode('taux_engagement') && (
                    <td className="numeric"><strong>{formatNumber(totals.tauxEngagement, 2)}%</strong></td>
                  )}
                  {isMetricVisibleInMode('clics') && (
                    <td className="numeric"><strong>{formatNumber(totals.clicks)}</strong></td>
                  )}
                  {isMetricVisibleInMode('cout') && (
                    <td className="numeric"><strong>€{formatNumber(totals.cost, 2)}</strong></td>
                  )}
                  {isMetricVisibleInMode('cpc') && (
                    <td className="numeric"><strong>€{formatNumber(totals.cpc, 2)}</strong></td>
                  )}
                  {isMetricVisibleInMode('conv') && (
                    <td className="numeric"><strong>{formatNumber(totals.conversions, 1)}</strong></td>
                  )}
                  {isMetricVisibleInMode('taux_conv') && (
                    <td className="numeric"><strong>{formatNumber(totals.conversionRate, 2)}%</strong></td>
                  )}
                  {isMetricVisibleInMode('cpa') && (
                    <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', 'youtube_videos')}`}><strong>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</strong></td>
                  )}
                  {isMetricVisibleInMode('valeur_conv') && (
                    <td className="numeric"><strong>€{formatNumber(totals.conversionsValue, 2)}</strong></td>
                  )}
                  {isMetricVisibleInMode('cos') && (
                    <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', 'youtube_videos')}`}>
                      <strong>{formatNumber(totals.cos, 1)}%</strong>
                    </td>
                  )}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Contrôles de pagination */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                <span>
                  Affichage {startIndex + 1}-{Math.min(endIndex, sortedVideos.length)} sur {sortedVideos.length} vidéos
                </span>
              </div>
              <div className="pagination-buttons">
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  ← Précédent
                </button>
                <span className="page-numbers">
                  Page {currentPage} sur {totalPages}
                </span>
                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Suivant →
                </button>
              </div>
            </div>
              )}
            </>
          )}
        </>
      )}
      
      {/* Tooltip personnalisé */}
      {activeTooltip && (
        <div
          style={{
            position: 'fixed',
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translate(-50%, 0)',
            backgroundColor: '#333',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '6px',
            fontSize: '0.85em',
            maxWidth: activeTooltip === 'views' ? '400px' : activeTooltip === 'conv' ? '350px' : activeTooltip === 'cpv' ? '350px' : '300px',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            pointerEvents: 'none',
            whiteSpace: 'pre-line',
            lineHeight: '1.4'
          }}
        >
          {getTooltipContent(activeTooltip)}
        </div>
      )}
    </div>
  );
});

YoutubeVideosTable.displayName = 'YoutubeVideosTable';

export default YoutubeVideosTable;